import { useState } from 'react';
import {
  getObservabilityBridge,
  type ObservabilityResponse,
} from '../api/endpoints.js';
import { useApiQuery } from '../api/hooks.js';
import { useCan } from '../session/can.js';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  LoadingState,
  PageHead,
  Select,
  TBody,
  TD,
  THead,
  TH,
  TRow,
  Table,
} from '../ui/index.js';
import type { BadgeTone } from '../ui/index.js';

const ALL = '__all__';

const CATEGORIES: readonly string[] = [
  'delivery',
  'provider',
  'renewal',
  'session',
  'system',
  'pricing',
  'payment',
  'billing',
  'backpressure',
];

export function ObservabilityPage(): JSX.Element {
  const [category, setCategory] = useState<string>(ALL);
  const canRead = useCan('observability:read');

  const query = useApiQuery<ObservabilityResponse>(
    (options) =>
      getObservabilityBridge(options, category === ALL ? undefined : category),
    [category],
  );

  return (
    <div>
      <PageHead
        crumb={<>Yönetim <Icon.ChevronRight size={11} /> Gözlem ve Metrikler</>}
        title="Gözlem ve Metrikler"
        subtitle={
          <>
            Admin metrik hesaplamaz. Bu sayfa observability düzlemine (Grafana, Prometheus,
            Kibana) doğrudan bağlantılar ve platformla birlikte gelen kanonik metrik /
            SLO / uyarı kataloğunu gösterir. <em>Uyarı özeti</em> her kuralın güncel durumunu
            sergiler — <code>unknown</code>, köprünün canlı değerlendiriciye henüz
            bağlanmadığını ifade eder; sahte "ok" yoktur.
          </>
        }
        actions={
          <Button variant="secondary" icon={<Icon.Refresh size={14} />} onClick={() => query.reload()}>
            Yenile
          </Button>
        }
      />

      <Card>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="cat" className="field-label">Kategori</label>
            <Select id="cat" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 200 }}>
              <option value={ALL}>tümü</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {!canRead ? <ErrorState reason="capability_missing:observability:read" /> : null}
      {canRead && query.kind === 'loading' ? <LoadingState /> : null}
      {canRead && query.kind === 'error' ? <ErrorState reason={query.reason} onRetry={query.reload} /> : null}
      {canRead && query.kind === 'ok' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BridgeView bridge={query.data.bridge} correlationId={query.data.correlationId} />
        </div>
      ) : null}
    </div>
  );
}

function severityTone(s: string): BadgeTone {
  switch (s) {
    case 'ok': return 'success';
    case 'warning': return 'warn';
    case 'critical':
    case 'page': return 'danger';
    case 'info': return 'info';
    default: return 'neutral';
  }
}

function stateTone(s: string): BadgeTone {
  switch (s) {
    case 'ok': return 'success';
    case 'warning': return 'warn';
    case 'firing': return 'danger';
    default: return 'neutral';
  }
}

function BridgeView({
  bridge,
  correlationId,
}: {
  readonly bridge: ObservabilityResponse['bridge'];
  readonly correlationId: string;
}): JSX.Element {
  return (
    <>
      <Card flush>
        <CardHeader
          title="Köprü"
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <Badge tone={bridge.configured ? 'success' : 'warn'} dot>{bridge.configured ? 'configured' : 'not yet'}</Badge>
              <Badge tone={severityTone(bridge.overallSeverity)} dot data-testid="overall-severity-pill">
                {bridge.overallSeverity}
              </Badge>
            </div>
          }
        />
        <CardBody>
          <div className="dl-rows">
            <div className="dl-row"><div className="dl-key">Base URL</div><div className="dl-val t-mono t-xs">{bridge.baseUrl ?? '—'}</div></div>
            <div className="dl-row"><div className="dl-key">Observed at</div><div className="dl-val t-mono t-xs">{bridge.observedAt}</div></div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Banner tone="info"><span className="t-mono t-xs">correlation: {correlationId}</span></Banner>
          </div>
        </CardBody>
      </Card>

      <PanelsCard bridge={bridge} />
      <AlertsCard bridge={bridge} />
      <SlosCard bridge={bridge} />
      <MetricsCard bridge={bridge} />
    </>
  );
}

function PanelsCard({ bridge }: { readonly bridge: ObservabilityResponse['bridge'] }): JSX.Element {
  return (
    <Card flush>
      <CardHeader title="Paneller" />
      <CardBody tight style={{ padding: bridge.panels.length === 0 ? 20 : 0 }}>
        {bridge.panels.length === 0 ? (
          <EmptyState
            label="Bu kategoride panel yok."
            hint={bridge.configured ? 'Observability düzleminde henüz panel yok.' : 'Köprü konfigürasyonunu API tarafında bağlayın.'}
          />
        ) : (
          <Table dense>
            <THead>
              <TRow>
                <TH>Panel</TH>
                <TH>Kategori</TH>
                <TH>Kaynak</TH>
                <TH>Etiketler</TH>
                <TH>Link</TH>
              </TRow>
            </THead>
            <TBody>
              {bridge.panels.map((p) => (
                <TRow key={p.panelId}>
                  <TD>
                    <strong>{p.title}</strong>
                    <div className="t-tertiary t-xs">{p.description}</div>
                  </TD>
                  <TD className="t-mono t-xs">{p.category}</TD>
                  <TD className="t-mono t-xs">{p.sourceSystem}</TD>
                  <TD className="t-mono t-xs">{p.tags.join(', ')}</TD>
                  <TD>
                    {p.externalUrl ? (
                      <a href={p.externalUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        aç <Icon.ArrowUR size={11} />
                      </a>
                    ) : '—'}
                  </TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function AlertsCard({ bridge }: { readonly bridge: ObservabilityResponse['bridge'] }): JSX.Element {
  const summaryById = new Map(bridge.alertSummaries.map((s) => [s.alertId, s]));
  return (
    <Card flush data-testid="alerts-card">
      <CardHeader title="Uyarı roll-up" />
      <CardBody tight style={{ padding: bridge.alerts.length === 0 ? 20 : 0 }}>
        {bridge.alerts.length === 0 ? (
          <EmptyState label="Bu kategoride uyarı kuralı yok." />
        ) : (
          <Table dense>
            <THead>
              <TRow>
                <TH>Uyarı</TH>
                <TH>Severity</TH>
                <TH>Condition</TH>
                <TH>State</TH>
                <TH>Route</TH>
              </TRow>
            </THead>
            <TBody>
              {bridge.alerts.map((a) => {
                const summary = summaryById.get(a.alertId);
                const state = summary?.state ?? 'unknown';
                return (
                  <TRow key={a.alertId}>
                    <TD>
                      <strong>{a.title}</strong>
                      <div className="t-tertiary t-xs">{a.summary}</div>
                    </TD>
                    <TD><Badge tone={severityTone(a.severity)} dot>{a.severity}</Badge></TD>
                    <TD className="t-mono t-xs">{a.condition} · {a.threshold} · {a.windowMinutes}m</TD>
                    <TD><Badge tone={stateTone(state)} dot>{state}</Badge></TD>
                    <TD className="t-mono t-xs">{a.routeTo}</TD>
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function SlosCard({ bridge }: { readonly bridge: ObservabilityResponse['bridge'] }): JSX.Element {
  return (
    <Card flush data-testid="slos-card">
      <CardHeader title="SLO — service-level objectives" />
      <CardBody tight style={{ padding: bridge.slos.length === 0 ? 20 : 0 }}>
        {bridge.slos.length === 0 ? (
          <EmptyState label="Bu kategoride SLO yok." />
        ) : (
          <Table dense>
            <THead>
              <TRow><TH>SLO</TH><TH>Objective</TH><TH>Hedef</TH><TH>Pencere</TH><TH>Owner</TH></TRow>
            </THead>
            <TBody>
              {bridge.slos.map((s) => (
                <TRow key={s.sloId}>
                  <TD>
                    <strong>{s.title}</strong>
                    <div className="t-tertiary t-xs">{s.description}</div>
                  </TD>
                  <TD className="t-mono t-xs">{s.objective}</TD>
                  <TD className="t-mono t-xs">{s.target.target} {s.target.unit}</TD>
                  <TD className="t-mono t-xs">{s.target.windowHours}h</TD>
                  <TD className="t-mono t-xs">{s.owner}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}

function MetricsCard({ bridge }: { readonly bridge: ObservabilityResponse['bridge'] }): JSX.Element {
  return (
    <Card flush data-testid="metrics-card">
      <CardHeader title="Metrik kataloğu" />
      <CardBody tight style={{ padding: bridge.metrics.length === 0 ? 20 : 0 }}>
        {bridge.metrics.length === 0 ? (
          <EmptyState label="Bu kategoride metrik yok." />
        ) : (
          <Table dense>
            <THead>
              <TRow><TH>Metric</TH><TH>Kind</TH><TH>Unit</TH><TH>Required tags</TH></TRow>
            </THead>
            <TBody>
              {bridge.metrics.map((m) => (
                <TRow key={m.name}>
                  <TD>
                    <strong>{m.name}</strong>
                    <div className="t-tertiary t-xs">{m.description}</div>
                  </TD>
                  <TD className="t-mono t-xs">{m.kind}</TD>
                  <TD className="t-mono t-xs">{m.unit}</TD>
                  <TD className="t-mono t-xs">{m.requiredTags.join(', ') || '—'}</TD>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
}
